import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, RichFAQ, RelatedLinks, RichCTA,
} from '../RichBlocks';
import { EVChargingCalculator, PriceBreakdownBar } from '../RichCalculators';
import { TrustBadgesRow } from '@/components/seo/content/ContentBlocks';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function GennemsnitligElpris2026({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    { question: 'Hvad er den gennemsnitlige elpris i Danmark i 2026?', answer: 'Den samlede pris for en almindelig husstand ligger typisk omkring 2,2–2,6 kr./kWh inkl. transport, elafgift og moms. Den rå spotpris (det elselskabet køber strømmen til på Nord Pool) udgør kun ca. 0,7–1,2 kr./kWh – resten er netafgifter, elafgift og moms, som er ens uanset hvilket elselskab du vælger.' },
    { question: 'Hvad er forskellen mellem spotpris og den pris jeg betaler?', answer: 'Spotprisen er den rå markedspris på Nord Pool-børsen. Oveni lægger dit netselskab transport/nettarif, staten lægger elafgift, og til sidst kommer 25% moms. Dit elselskab tjener på et eventuelt tillæg pr. kWh og/eller et månedligt abonnement. Hos enkel energi er tillægget 0 øre.' },
    { question: 'Hvorfor svinger elprisen så meget?', answer: 'Spotprisen sættes time for time efter udbud og efterspørgsel. Når det blæser meget, er strømmen billig; i vindstille, kolde aftentimer er den dyr. Derfor kan den samme kWh koste under 0,30 kr. om natten og over 1,50 kr. i aftenspidsen.' },
    { question: 'Hvad er forskellen på DK1 og DK2?', answer: 'DK1 er Vestdanmark (Jylland og Fyn) og DK2 er Østdanmark (Sjælland, Lolland-Falster og Bornholm). De to zoner har hver deres spotpris, fordi der er begrænset kabelkapacitet imellem dem. enkel energi tilbyder spotpris uden tillæg i begge zoner.' },
    { question: 'Hvordan finder jeg det billigste elselskab?', answer: 'Sammenlign tre ting: tillægget pr. kWh, det månedlige abonnement og hvilke services der er inkluderet. Mange selskaber lokker med lavt abonnement men lægger 2–8 øre tillæg pr. kWh. Hos enkel er tillægget 0 øre, og Saving Mode, serviceaftale og laderabat er inkluderet.' },
    { question: 'Kan jeg gøre noget for at sænke min gennemsnitlige elpris?', answer: 'Ja. Flyt forbrug til billige timer (Saving Mode gør det automatisk for elbil-opladning), vælg et selskab uden tillæg, og udnyt laderabatten på 10 øre/kWh. For elbilejere kan det reducere den effektive kWh-pris til opladning med 20–30%.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'sammensaetning', label: 'Prisens sammensætning' },
    { id: 'udvikling', label: 'Prisudvikling' },
    { id: 'billigst', label: 'Sådan bliver du billigst' },
    { id: 'elbil', label: 'Elbil-beregner' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Gennemsnitlig elpris 2026', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Gennemsnitlig elpris i Danmark 2026" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Data & priser · 2026"
        h1="Gennemsnitlig elpris i Danmark 2026"
        intro="Hvad koster en kWh strøm i Danmark i 2026 – og hvor stor en del kan du faktisk selv påvirke? Vi bryder prisen ned post for post og viser, hvordan du kommer ned blandt de billigste."
        badges={['Spotpris + 0 øre', 'Opdateret 2026', 'Uafhængigt testet 5/5']}
        updated={updated}
        image="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '~2,2–2,6', label: 'kr./kWh – samlet pris for en husstand' },
          { value: '~0,7–1,2', label: 'kr./kWh – ren spotpris (Nord Pool)' },
          { value: '0 øre', label: 'tillæg til spotpris hos enkel' },
          { value: '18 kr.', label: 'abonnement/md. (enkel kvartal)' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Hvad er den gennemsnitlige elpris i 2026?">
        <AnswerBox question="Hvad koster en kWh el i Danmark i 2026?">
          For en almindelig husstand ligger den samlede pris typisk på <strong>2,2–2,6 kr./kWh</strong> inkl.
          transport, elafgift og moms. Selve den rå <strong>spotpris</strong> er kun ca. <strong>0,7–1,2 kr./kWh</strong>.
          Resten er faste afgifter, der er ens uanset elselskab. Det du selv kan påvirke er
          <strong> tillægget pr. kWh</strong> og <strong>abonnementet</strong> – og her er enkel energi blandt de billigste med 0 øre tillæg fra 18 kr./md.
        </AnswerBox>
        <p>
          Når man taler om "den gennemsnitlige elpris", blander mange to ting sammen: spotprisen og den endelige regning.
          Spotprisen fastsættes løbende time for time på Nord Pool-børsen i både DK1 (Vestdanmark) og DK2 (Østdanmark). Den udgør faktisk
          kun en mindre del af det beløb, der står på din elregning. Langt størstedelen er transport (nettarif til dit lokale netselskab),
          statens elafgift og 25% moms.
        </p>
        <p>
          For de fleste forbrugere er det afgørende at forstå, at de over 50 danske elselskaber leverer <em>nøjagtig samme strøm</em> –
          forskellen ligger i, hvor meget de lægger oveni. Mange selskaber tager et tillæg på 2–8 øre pr. kWh oveni spotprisen.
          Ved et årligt forbrug på 9.000 kWh kan det tillæg alene koste mellem 180 og 720 kr. om året. Hos enkel energi er tillægget 0 øre.
        </p>
      </ProseSection>

      <ProseSection id="sammensaetning" heading="Sådan er kWh-prisen sammensat" alt>
        <p>Den enkleste måde at forstå din elpris på er at se den som fire lag. Kun de to nederste kan du selv vælge imellem:</p>
        <div className="my-6"><PriceBreakdownBar /></div>
        <FactGrid facts={[
          { value: 'Spotpris', label: 'Markedspris på Nord Pool', sub: 'Svinger time for time' },
          { value: 'Nettarif', label: 'Transport via dit netselskab', sub: 'Fast – samme uanset elselskab' },
          { value: 'Elafgift', label: 'Statslig afgift', sub: 'Fast sats' },
          { value: 'Moms 25%', label: 'Lægges oveni alt', sub: 'Fast' },
        ]} columns={4} />
        <p className="mt-6">
          Pointen: Når du skifter elselskab, ændrer du <strong>ikke</strong> på nettarif, elafgift eller moms. Du ændrer kun på
          selskabets tillæg og abonnement. Derfor er den billigste vej til en lav gennemsnitlig elpris at vælge et selskab med
          0 øre tillæg og et lavt, gennemskueligt abonnement.
        </p>
      </ProseSection>

      <ProseSection id="udvikling" heading="Prisudvikling – hvorfor svinger elprisen?">
        <p>
          2024 og 2025 bød på de laveste gennemsnitspriser i flere år, primært drevet af mere vindkraft i nettet og lavere gaspriser i Europa.
          I 2026 er billedet fortsat præget af store udsving inden for det enkelte døgn frem for ekstreme prisspidser. Det betyder, at
          <strong> hvornår</strong> du bruger strømmen er blevet næsten lige så vigtigt som <strong>hvor meget</strong> du bruger.
        </p>
        <p>
          For en elbilejer er det godt nyt: forskellen mellem den billigste nattetime og den dyreste aftentime kan være 1–3 kr./kWh.
          Ved at flytte opladningen til de billige timer – automatisk via Saving Mode – kan man derfor reducere den effektive opladningspris betydeligt.
        </p>
        <AnswerBox>
          Den gennemsnitlige elpris er ikke ét tal, men et gennemsnit af 8.760 timepriser om året. To husstande med samme forbrug kan
          ende med vidt forskellige regninger alt efter, <em>hvornår på døgnet</em> de bruger strømmen.
        </AnswerBox>
      </ProseSection>

      <ProseSection id="billigst" heading="Sådan kommer du ned blandt de billigste" alt>
        <p>Hos enkel energi tilbyder vi en gennemsigtig model: spotpris + 0 øre i tillæg, med tre simple aftaler uden binding eller depositum.</p>
        <div className="my-6">
          <ComparisonTable
            caption="Sammenligning af enkel energi-aftaler"
            columns={[
              { name: 'enkel kvartal', highlight: true },
              { name: 'enkel strøm' },
              { name: 'enkel natur' },
            ]}
            rows={[
              { label: 'Abonnement/md.', values: ['18 kr.', '28 kr.', '48 kr.'] },
              { label: 'Tillæg til spotpris', values: ['0 øre', '0 øre', '0 øre'] },
              { label: 'Afregning', values: ['Kvartal', 'Måned', 'Måned'] },
              { label: 'Binding', values: ['Nej', 'Nej', 'Nej'] },
              { label: 'Saving Mode', values: [true, true, true] },
              { label: 'Laderabat 10 øre/kWh', values: [true, true, true] },
              { label: 'Støtter naturprojekter', values: [false, false, '20 kr./md.'] },
            ]}
          />
        </div>
        <p>
          Vores priser er anerkendt udefra: NicolasKJ.dk har kåret os som billigst, ELBIIL.dk gav os titlen som bedste ladeløsning, og i en
          stor uafhængig forbrugertest af el-aftaler i 2026 lå hele tre af vores produkter i top 10.
        </p>
        <div className="mt-6"><TrustBadgesRow variant="light" /></div>
      </ProseSection>

      <ProseSection id="elbil" heading="Har du elbil? Så kan din effektive elpris blive markant lavere">
        <p>
          For elbilejere er den gennemsnitlige elpris tæt forbundet med, hvornår bilen lades. Med enkel Saving Mode flyttes opladningen
          automatisk til de billigste timer, og du får en laderabat på 10 øre pr. kWh oveni på de kWh, Saving Mode styrer (kompatibel ladeboks og bil kræves). Prøv beregneren:
        </p>
        <div className="my-6"><EVChargingCalculator /></div>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/elpris-sammenligning', label: 'Sammenlign elselskaber' },
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
        { path: '/hvad-koster-el-gennem-enkel', label: 'Hvad koster el hos enkel?' },
        { path: '/forstaa-din-elregning', label: 'Forstå din elregning' },
        { path: '/portal/smart-charge', label: 'Saving Mode' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Få en lav, gennemsigtig elpris" subtext="Spotpris + 0 øre fra 18 kr./md. Ingen binding. Vi opsiger dit gamle selskab for dig." />
      <SEODisclaimer page={page} />
    </article>
  );
}